import { FormHandles, useField } from '@unform/core';
import { ptBR } from 'date-fns/locale';
import { formatDistanceToNow } from 'date-fns';
import React, {
  InputHTMLAttributes,
  TextareaHTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { Painel } from '../../../../components/Painel';
import { InputFileExercise } from '../../../../components/InputFileExercise';
import { optionProps } from '../../../../components/Select';
import { CardMuscleGroup } from '../../../../components/CardMuscleGroup';
import {
  ComingSoonTag,
  DisabledField,
} from '../../../../components/ComingSoonTag';

import { useToast } from '../../../../hooks/toast';
import { getValidationErrors } from '../../../../utils/getValidationErrors';
import { ExercisesService } from '../../../../services/ExercisesService';
import {
  IExercise,
  IExerciseMedia,
  IExerciseMuscleGroup,
  IMuscleGroup,
} from '../../../../domains/Exercise';

import {
  AutoSaveTag,
  Breadcrumb,
  Card,
  CardHeader,
  CardTitle,
  Container,
  ContainerCardsMuscleGroups,
  Discard,
  DescriptionArea,
  DifficultyBar,
  DifficultyBars,
  DifficultyLabel,
  DifficultyRow,
  FieldGroup,
  FieldLabel,
  FormContainer,
  Grid,
  Header,
  HeaderActions,
  MainColumn,
  MediaRow,
  MuscleGroupLabel,
  MuscleGroupSection,
  PageTitle,
  PulseDot,
  SaveButton,
  SelectField,
  Sidebar,
  SidebarCard,
  SidebarTitle,
  StaticValue,
  TitleInput,
  UsageGrid,
  UsageHeader,
  UsageStat,
  UsageStatLabel,
  UsageStatValue,
} from './styles';

interface IFormProps {
  name: string;
  description: string;
}

interface locationState {
  exercise?: IExercise;
}

interface fieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'ref'> {
  name: string;
}

const TitleField: React.FC<fieldProps> = ({ name, ...rest }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { fieldName, defaultValue, registerField } = useField(name);

  useEffect(() => {
    registerField({ name: fieldName, ref: inputRef.current, path: 'value' });
  }, [fieldName, registerField]);

  return <TitleInput ref={inputRef} defaultValue={defaultValue} {...rest} />;
};

interface textAreaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'ref'> {
  name: string;
}

const DescriptionField: React.FC<textAreaFieldProps> = ({ name, ...rest }) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { fieldName, defaultValue, registerField } = useField(name);

  useEffect(() => {
    registerField({ name: fieldName, ref: inputRef.current, path: 'value' });
  }, [fieldName, registerField]);

  return (
    <DescriptionArea ref={inputRef} defaultValue={defaultValue} {...rest} />
  );
};

const ExerciseDetails: React.FC = () => {
  const exercisesService = new ExercisesService();
  const formRef = useRef<FormHandles>(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();
  const { addToast } = useToast();

  const [exercise, setExercise] = useState<IExercise | undefined>(
    (state as locationState)?.exercise,
  );
  const [muscleGroups, setMuscleGroups] = useState<IMuscleGroup[]>([]);
  const [listMuscleGroups, setListMuscleGroups] = useState<
    IExerciseMuscleGroup[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | undefined>();

  const getMuscleGroups = useCallback(() => {
    exercisesService
      .getMuscleGroups()
      .then(response => {
        setMuscleGroups(response.data);
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro na busca dos grupos musculares.',
          description:
            'Caso o proplema persista, entre em contato com o administrador do sistema',
        });
      });
  }, []);

  useEffect(() => {
    getMuscleGroups();
  }, []);

  useEffect(() => {
    if (exercise) {
      setListMuscleGroups(exercise.muscle_groups);
      return;
    }

    if (!id) return;

    exercisesService
      .getExerciseById(id)
      .then(response => {
        setExercise(response.data);
        setListMuscleGroups(response.data.muscle_groups);
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Exercício não encontrado.',
        });
        navigate('/exercises');
      });
  }, [id]);

  useEffect(() => {
    if (!id) return;

    exercisesService
      .getExerciseById(id)
      .then(response => {
        setExercise(current =>
          current ? { ...current, usage: response.data.usage } : current,
        );
      })
      // uso ativo é informativo: falha aqui não deve travar a tela
      .catch(() => undefined);
  }, [id]);

  const isCustom = exercise ? exercise.is_custom : true;

  const mainGroup = listMuscleGroups.find(group => group.is_main);
  const mainGroupName = mainGroup?.name;
  const secondaryGroups = listMuscleGroups.filter(group => !group.is_main);

  const addItemMuscleGroup = useCallback(
    (selected: optionProps) => {
      if (
        !listMuscleGroups.find(
          group => group.muscle_group_id === selected.value,
        )
      ) {
        const groupfinded = muscleGroups.find(
          finded => finded.id === selected.value,
        );
        if (groupfinded) {
          setListMuscleGroups(element => [
            ...element,
            {
              name: selected.label,
              muscle_group_id: selected.value,
              is_main: false,
            } as IExerciseMuscleGroup,
          ]);
        }
      }
    },
    [listMuscleGroups, muscleGroups],
  );

  const handleChangeSelect = useCallback(
    (e: any) => {
      const option: optionProps = e;
      if (option) {
        addItemMuscleGroup(option);
      }
    },
    [addItemMuscleGroup],
  );

  const handleSubmit = useCallback(
    async (data: IFormProps) => {
      if (!exercise) return;

      try {
        setLoading(true);

        if (listMuscleGroups.length < 1) {
          addToast({
            type: 'error',
            title: 'Pelo menos um grupo muscular deve ser cadastrado.',
          });
          setLoading(false);
          return;
        }

        if (!listMuscleGroups.some(m => m.is_main)) {
          addToast({
            type: 'error',
            title: 'Pelo menos um grupo muscular deve ser o principal.',
          });
          setLoading(false);
          return;
        }

        if (listMuscleGroups.filter(m => m.is_main).length > 1) {
          addToast({
            type: 'error',
            title: 'Apenas um grupo muscular pode ser o principal.',
          });
          setLoading(false);
          return;
        }

        formRef.current?.setErrors({});

        const schema = Yup.object({
          name: Yup.string().required('Campo obrigatório'),
          description: Yup.string(),
        });

        await schema.validate(data, { abortEarly: false });

        const response = await exercisesService.updateExercise({
          id: exercise.id,
          name: data.name,
          muscle_groups: listMuscleGroups,
          description: data.description || undefined,
        });

        setExercise(response.data);
        setLastSavedAt(new Date());

        addToast({ type: 'success', title: 'Exercício alterado com sucesso.' });

        setLoading(false);
      } catch (err: any) {
        setLoading(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }

        if (err?.response?.status === 403) {
          addToast({
            type: 'error',
            title: 'Você não tem permissão para executar essa ação.',
          });
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro na atualização',
          description:
            'Houve um erro na tentativa de atualização do exercicio. Tente novamente mais tarde.',
        });
      }
    },
    [addToast, exercise, listMuscleGroups],
  );

  const autoSaveLabel = useMemo(() => {
    if (!lastSavedAt) return undefined;
    return `Salvo ${formatDistanceToNow(lastSavedAt, {
      addSuffix: true,
      locale: ptBR,
    })}`;
  }, [lastSavedAt]);

  return (
    <Painel loading={!exercise}>
      <Container>
        <Header>
          <div>
            <Breadcrumb>
              <span>Biblioteca</span>
              <span>{mainGroupName || 'Sem classificação'}</span>
            </Breadcrumb>
            <PageTitle>
              {exercise?.name || 'Exercício'}
              {!isCustom && (
                <ComingSoonTag label="Biblioteca padrão" variant="suggested" />
              )}
            </PageTitle>
          </div>
          <HeaderActions>
            <Discard type="button" onClick={() => navigate('/exercises')}>
              Descartar
            </Discard>
            {isCustom && (
              <SaveButton
                type="button"
                disabled={loading}
                onClick={() => formRef.current?.submitForm()}
              >
                Salvar exercício
              </SaveButton>
            )}
          </HeaderActions>
        </Header>

        {!!exercise && (
          <FormContainer
            ref={formRef}
            initialData={exercise}
            onSubmit={handleSubmit}
          >
            <Grid>
              <MainColumn>
                <MediaRow>
                  {[0, 1].map(position => (
                    <InputFileExercise
                      key={position}
                      exercise_id={exercise.id}
                      position={position as 0 | 1}
                      media={exercise.media?.find(
                        item => item.position === position,
                      )}
                      onChange={(updated: IExerciseMedia | undefined) =>
                        setExercise(current => {
                          if (!current) return current;

                          const media = (current.media || []).filter(
                            item => item.position !== position,
                          );

                          if (updated) {
                            media.push(updated);
                          }

                          return { ...current, media };
                        })
                      }
                    />
                  ))}
                </MediaRow>

                <Card>
                  <CardHeader>
                    <CardTitle>Instruções técnicas</CardTitle>
                    {!!autoSaveLabel && (
                      <AutoSaveTag>{autoSaveLabel}</AutoSaveTag>
                    )}
                  </CardHeader>

                  <FieldGroup style={{ marginBottom: 24 }}>
                    <FieldLabel>Título interno</FieldLabel>
                    <TitleField name="name" disabled={!isCustom} />
                  </FieldGroup>

                  <FieldGroup>
                    <FieldLabel>Descrição técnica</FieldLabel>
                    <DescriptionField
                      name="description"
                      rows={6}
                      placeholder="Explique como executar o exercício"
                      disabled={!isCustom}
                    />
                  </FieldGroup>
                </Card>
              </MainColumn>

              <Sidebar>
                <SidebarCard>
                  <SidebarTitle>Grupos musculares</SidebarTitle>

                  <MuscleGroupSection>
                    <MuscleGroupLabel>Foco principal</MuscleGroupLabel>
                    <ContainerCardsMuscleGroups>
                      {mainGroup && (
                        <CardMuscleGroup
                          key={mainGroup.id}
                          label={mainGroup?.name as string}
                          isMain
                          onClickClose={
                            isCustom
                              ? () =>
                                  setListMuscleGroups(groups => [
                                    ...groups.filter(
                                      current => current.id !== mainGroup.id,
                                    ),
                                  ])
                              : undefined
                          }
                        />
                      )}
                    </ContainerCardsMuscleGroups>
                  </MuscleGroupSection>

                  <MuscleGroupSection>
                    <MuscleGroupLabel>Suporte secundário</MuscleGroupLabel>
                    <ContainerCardsMuscleGroups>
                      {secondaryGroups.map(group => (
                        <CardMuscleGroup
                          key={group.id}
                          label={group?.name as string}
                          isMain={false}
                          onClickClose={
                            isCustom
                              ? () =>
                                  setListMuscleGroups(groups => [
                                    ...groups.filter(
                                      current => current.id !== group.id,
                                    ),
                                  ])
                              : undefined
                          }
                          onClickSetMain={
                            isCustom
                              ? () => {
                                  setListMuscleGroups(groups =>
                                    groups.map(current => ({
                                      ...current,
                                      is_main: current.id === group.id,
                                    })),
                                  );
                                }
                              : undefined
                          }
                        />
                      ))}

                      {isCustom && (
                        <SelectField
                          clearValueChange
                          name="muscle_groups"
                          options={muscleGroups.map(group => ({
                            label: group.name as string,
                            value: group.id as string,
                          }))}
                          onChange={handleChangeSelect}
                          placeholder="+ Adicionar"
                        />
                      )}
                    </ContainerCardsMuscleGroups>
                  </MuscleGroupSection>
                </SidebarCard>

                <Card>
                  <SidebarTitle>
                    Equipamento e dificuldade
                    <ComingSoonTag />
                  </SidebarTitle>

                  <DisabledField>
                    <FieldGroup style={{ marginBottom: 16 }}>
                      <FieldLabel>Equipamento necessário</FieldLabel>
                      <SelectField
                        name="equipment"
                        options={[]}
                        placeholder="Selecionar equipamento"
                      />
                    </FieldGroup>

                    <FieldGroup style={{ marginBottom: 16 }}>
                      <FieldLabel>Nível de dificuldade</FieldLabel>
                      <DifficultyRow>
                        <DifficultyBars>
                          <DifficultyBar active />
                          <DifficultyBar active />
                          <DifficultyBar />
                          <DifficultyBar />
                          <DifficultyBar />
                        </DifficultyBars>
                        <DifficultyLabel>—</DifficultyLabel>
                      </DifficultyRow>
                    </FieldGroup>

                    <FieldGroup>
                      <FieldLabel>Descanso recomendado</FieldLabel>
                      <StaticValue>—</StaticValue>
                    </FieldGroup>
                  </DisabledField>
                </Card>

                <Card>
                  <UsageHeader>
                    <PulseDot />
                    <SidebarTitle style={{ marginBottom: 0 }}>
                      Uso ativo
                    </SidebarTitle>
                  </UsageHeader>

                  <UsageGrid>
                    <UsageStat>
                      <UsageStatLabel>Em programas</UsageStatLabel>
                      <UsageStatValue>
                        {exercise?.usage?.inPrograms ?? '—'}
                      </UsageStatValue>
                    </UsageStat>
                    <UsageStat>
                      <UsageStatLabel>Recorde global</UsageStatLabel>
                      <UsageStatValue>
                        {exercise?.usage?.globalPr
                          ? `${exercise.usage.globalPr}kg`
                          : '—'}
                      </UsageStatValue>
                    </UsageStat>
                  </UsageGrid>
                </Card>
              </Sidebar>
            </Grid>
          </FormContainer>
        )}
      </Container>
    </Painel>
  );
};

export { ExerciseDetails };
